import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { inViewOnce, reveal } from '../utils/motion';
import type { CaseStudy } from '../data/caseStudies';
import { caseStudyMetrics } from '../data/caseStudyMetrics';
type CaseStudyCardProps = {
  study: CaseStudy;
  /** "compact" always stacks text above the image at a smaller scale — used for previews. */
  size?: 'default' | 'compact';
  /** Slide text in from the left and imagery from the right when the card scrolls into view. */
  animate?: boolean;
};
const shadow = 'shadow-[0px_0px_20px_6px_rgba(0,0,0,0.1)]';
export function CaseStudyCard({
  study,
  size = 'default',
  animate = false
}: CaseStudyCardProps) {
  const compact = size === 'compact';
  const reduceMotion = useReducedMotion();
  const slideIn = (fromX: number, delay = 0) => animate ? {
    initial: {
      opacity: 0,
      x: fromX
    },
    whileInView: {
      opacity: 1,
      x: 0
    },
    viewport: inViewOnce,
    transition: {
      ...reveal,
      delay
    }
  } : {};
  const pad = compact ? 'p-6' : 'p-[30px]';
  const imagePad = compact ? 'px-6' : 'px-[30px] lg:pl-0 lg:pt-[30px]';
  const phonePad = compact ? 'px-6' : 'pl-[30px] pr-[30px] lg:pl-0 lg:pt-[30px]';
  const Heading = compact ? 'h3' : 'h2';
  const metrics = caseStudyMetrics[study.id] ?? [];
  return <article className={`flex h-full flex-col overflow-hidden ${compact ? 'rounded-[24px]' : 'rounded-[30px] lg:flex-row'}`} style={{
    background: study.background
  }} data-id="element-23" data-mp="2ynbtyn16r1yu">
      <motion.div className={`flex flex-col gap-[10px] ${pad} ${compact ? '' : 'lg:w-[40%] lg:min-w-[300px] lg:shrink-0'}`} {...slideIn(-40)} data-id="element-24" data-mp="38h13rrsqs2hx">
        <p className={`${compact ? 'text-sm' : 'text-base'} font-bold leading-6`} data-id="element-25" data-mp="3hgxwq5m5znis">{study.client}</p>
        <Heading className={compact ? 'text-[22px] leading-[1.25] tracking-[-0.01em]' : 'text-[24px] leading-[1.25] tracking-[-0.01em] md:text-[28px]'} data-id="element-26" data-mp="1s1hnkq88q1sw">
          {study.title}
        </Heading>
        {study.href.startsWith('/') ? <Link to={study.href} className="mt-2 inline-flex w-fit items-center whitespace-nowrap rounded-full bg-black px-4 py-2 text-sm font-medium leading-5 text-white transition-[background-color,transform] duration-150 ease-out hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 active:scale-[0.97]" data-id="element-27" data-mp="3s3hjnonennmi">
            View Case Study →
          </Link> : <span className="w-fit text-base leading-6 text-muted" data-id="element-28" data-mp="jmw5zujtw6d2">Case study coming soon</span>}
        {!compact && metrics.length > 0 && <ul aria-label={`${study.client} results`} className="mt-auto grid grid-cols-3 gap-4 pt-5 text-ink" data-id="element-29" data-mp="nqu5i6d2fj6n">
            {metrics.map(m => <li key={m.label} className="flex flex-col gap-1 [container-type:inline-size]" data-id="element-30" data-mp="xm492yj43ymj">
                <span className="whitespace-nowrap text-[clamp(18px,19.2cqw,28.8px)] leading-[1.2] tracking-[-0.02em]" data-id="element-31" data-mp="17rdni5lecyxe">
                  {m.value}
                </span>
                <span className="text-[13px] leading-[1.3]" data-id="element-32" data-mp="12u3wilid0f7g">{m.label}</span>
              </li>)}
          </ul>}
      </motion.div>

      {study.layout === 'phones' ? <motion.div className={`mt-auto flex min-w-0 flex-1 items-end gap-[3%] self-end ${phonePad}`} {...slideIn(60, 0.06)} data-id="element-33" data-mp="tt5qxys9vzmr">
          {study.images.map((src, i) => {
        const className = `aspect-[243/470] min-w-0 flex-1 object-cover object-top ${compact ? 'rounded-t-[14px]' : 'rounded-t-[20px]'} ${shadow}`;
        const video = study.videos?.[i];
        if (video) {
          return <video key={video} src={video} poster={src} className={className} autoPlay={!reduceMotion} muted loop playsInline preload="metadata" aria-label={`${study.client} mobile screen ${i + 1} recording`} data-id="element-34" data-mp="2983d4ajc5oqi" />;
        }
        return <img key={src} src={src} alt={`${study.client} mobile screen ${i + 1}`} className={className} data-id="element-35" data-mp="1g0i2hiezl9p0" data-mpi={i} />;
      })}
        </motion.div> : <motion.div className={`mt-auto flex min-w-0 flex-1 items-end justify-end self-end ${imagePad}`} {...slideIn(60, 0.06)} data-id="element-36" data-mp="27pbh5bpqqewh">
          {study.videos?.[0] ? <video src={study.videos[0]} poster={study.images[0]} className={`block aspect-[777/470] w-full object-cover object-left-top ${shadow} rounded-t-[10px]`} autoPlay={!reduceMotion} muted loop playsInline preload="metadata" aria-label={`${study.client} product screen recording`} data-id="element-37" data-mp="1yx9er7w42ojk" /> : <img src={study.images[0]} alt={`${study.client} product screen`} className={`block aspect-[777/470] w-full object-cover object-left-top ${shadow} rounded-t-[10px]`} data-id="element-38" data-mp="jyvs0qc42mwq" />}
        </motion.div>}
    </article>;
}